import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../contexts/ToastContext";
import { useUser } from "../../../contexts/UserContext/UserContext";
import {
    getCompanyId,
    isBookingAgentUrlConfigured,
    sendInsuranceSalePaymentLink,
} from "../lib/insuranceSaleApi";
import { isSolaGateway, sendSolaPaymentLink } from "../../../lib/sola/solaPaymentApi";
import { getBookingSiteOrigin } from "../../../lib/sola/bookingDomains";

const useInsuranceSaleSendPaymentLink = () => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const { myCompanyData } = useUser();
    const [loading, setLoading] = useState(false);
    const isSola = isSolaGateway(myCompanyData);
    // Stripe links need the booking agent URL; Sola links go to the company's own booking
    // domain from the domain map, so that is what has to be set up instead.
    const bookingAgentConfigured = isSola
        ? Boolean(getBookingSiteOrigin(getCompanyId(myCompanyData)))
        : isBookingAgentUrlConfigured();

    const sendPaymentLink = async ({
        reservationId,
        amount,
        customerEmail,
        reservationProcess = "insuranceSale",
        extensionDropoffDate,
        onSuccess,
    }) => {
        if (!bookingAgentConfigured) {
            toast.error({ message: t("pay.bookingAgentRequired") });
            return false;
        }

        if (!customerEmail?.trim()) {
            toast.error({ message: t("pay.noCustomerEmail") });
            return false;
        }

        setLoading(true);
        try {
            const companyId = getCompanyId(myCompanyData);
            const response = isSola
                ? await sendSolaPaymentLink({
                      reservationId,
                      reservationProcess,
                      amount,
                      customerEmail,
                      companyId,
                      extensionDropoffDate,
                  })
                : await sendInsuranceSalePaymentLink({
                      reservationId,
                      amount,
                      customerEmail,
                      companyId,
                      reservationProcess,
                      extensionDropoffDate,
                  });

            if (response?.success === false) {
                throw new Error(response?.message || t("pay.failedToSendPaymentLink"));
            }

            toast.success({ message: t("pay.paymentLinkSent") });
            onSuccess?.();
            return true;
        } catch (error) {
            toast.error({
                message: t("pay.failedToSendPaymentLink"),
                description: error?.response?.data?.message || error?.message,
            });
            return false;
        } finally {
            setLoading(false);
        }
    };

    return { sendPaymentLink, loading, bookingAgentConfigured };
};

export default useInsuranceSaleSendPaymentLink;
